<script>
    import { apiUrl } from "@/utils";
    import { sendAnalytics } from "@/core/SendAnalytics";
    import ProductCardButtons from "@/components/ProductCardButtons";
    import ProductColorsList from "@/components/ProductColorsList";
    import ProductPrice from "@martinelia/components/ProductPrice";
    import { SERIES } from "@martinelia/series";

    export default {
        name: "ProductCard",
        components: {
            ProductPrice,
            ProductCardButtons,
            ProductColorsList,
        },
        props: {
            product: Object,
            position: Number,
            isSmall: Boolean,
            inCartList: Array,
        },
        data() {
            let isPreOrder = false;
            let currentOffers = this.product.offers.filter((offer) => offer.available);

            if (currentOffers.length === 0) {
                // проверка на предзаказ
                currentOffers = this.product.offers.filter((offer) => !offer.available);
                isPreOrder = true;
            }

            const previewImage =
                currentOffers[0].previewImage?.medium || this.product.previewImage?.medium;

            return {
                currentOffers,
                offer: currentOffers[0],
                offerId: currentOffers[0].id,
                previewImage,
                price: currentOffers[0].price,
                discountPrice: currentOffers[0].discountPrice,
                discountPercent: currentOffers[0].discountPercent,
                inCart: this.product.inCart || false,
                isPreOrder,
                previewImageFromOffer: "",
                offerForParams: !this.product.isClone ? undefined : currentOffers[0].id,
            };
        },
        watch: {
            inCartList() {
                this.checkProductInCart();
            },
            offerId() {
                this.checkProductInCart();
            },
        },
        computed: {
            productName() {
                return this.product.shortName ? this.product.shortName : this.product.name;
            },
            sizeButton() {
                return this.isSmall ? "sm" : "md";
            },
            seriesName() {
                return SERIES[this.product.series];
            },
            imageSrc() {
                return this.previewImageFromOffer ? apiUrl(this.previewImageFromOffer) : "";
            },
            productPath() {
                return {
                    name: "Product",
                    params: { slug: this.product.slug, offer: this.offerId },
                };
            },
        },
        mounted() {
            this.checkProductInCart();
        },
        methods: {
            apiUrl,
            selectColor(color) {
                let { id, previewImage, price, discountPrice, available } =
                    this.currentOffers.filter((offer) => offer.color.id === color.id)[0];
                this.offerId = id;
                this.previewImageFromOffer = previewImage.medium;
                this.price = price;
                this.discountPrice = discountPrice;
                this.isPreOrder = !available;
                this.inCart = false;
            },
            checkProductInCart() {
                this.inCartList
                    ? (this.inCart = this.inCartList.includes(this.offerId.toString()))
                    : (this.inCart = false);
            },
            sendDataLayerEvent() {
                sendAnalytics.send(sendAnalytics.events.productClick, {
                    name: this.product.name,
                    offerId: this.offerId,
                    price: this.discountPrice,
                    position: this.position,
                });
            },
        },
    };
</script>

<template>
    <div
        class="product-card"
        :class="{ 'product-card--sm': isSmall }"
        :data-series="product.series"
    >
        <div v-if="seriesName" class="product-card__series">{{ seriesName }}</div>

        <div class="product-card__header">
            <div class="product-card__img">
                <router-link
                    :to="productPath"
                    class="product-card__img-link js-lazy-load"
                    tabindex="-1"
                    @click.native.prevent="sendDataLayerEvent"
                >
                    <img
                        class="product-card__img-product"
                        :src="imageSrc"
                        :data-src="apiUrl(previewImage)"
                    />
                    <div class="product-card__preloader">
                        <preloader :show="true" lg-size></preloader>
                    </div>
                </router-link>
            </div>

            <div v-if="offer.color && currentOffers.length > 1" class="product-card__colors">
                <product-colors-list
                    :offers="currentOffers"
                    :current-offer="offer"
                    @select-color="selectColor"
                ></product-colors-list>
            </div>
        </div>

        <router-link
            :to="productPath"
            class="product-card__wrap"
            @click.native.prevent="sendDataLayerEvent"
        >
            <div class="product-card__name">
                {{ productName }}
            </div>

            <div class="product-card__price">
                <product-price
                    :price="discountPrice"
                    :old-price="price"
                    :discount-count="discountPercent"
                    is-card-size
                ></product-price>
            </div>
        </router-link>

        <product-card-buttons
            :size-button="sizeButton"
            :offer-id="offerId"
            :product="product"
            :is-pre-order="isPreOrder"
            :in-cart="inCart"
            :discount-price="discountPrice"
            initial-text="В корзину"
            @on-added-to-cart="inCart = true"
        ></product-card-buttons>
    </div>
</template>

<style lang="scss">
    .product-card {
        position: relative;
        display: flex;
        flex-direction: column;
        height: auto;
        background-color: #fff;
        box-shadow: 0 0 10px #f6ecf6;
        border-radius: 12px;
        transition: 0.3s ease-in-out all;
        @include bp($bp-desktop-sm) {
            box-shadow: 0 0 25px #f6ecf6;
            border-radius: 20px;
        }

        &:hover {
            box-shadow: 0 0 0 1px #ffe8f6;

            .product-card__img {
                background-color: #ffe8f6;
            }

            .product-card__img-link {
                img {
                    transform: scale(1);
                }
            }
        }
    }

    .product-card__series {
        position: absolute;
        z-index: 7;
        top: 0;
        right: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 4px 5px;
        font-size: 12px;
        line-height: 16px;
        white-space: nowrap;
        text-align: center;
        background: #ffffff;
        border-radius: 0 12px 0 12px;
        pointer-events: none;
        @include bp($bp-desktop-sm) {
            top: 2px;
            right: 2px;
            padding: 7px 10px;
            font-size: 14px;
            border-radius: 0 20px 0 20px;
        }
    }

    .product-card__header {
        position: relative;
        padding: 4px 4px 0;
        @include bp($bp-desktop-sm) {
            padding: 12px 12px 0;
        }
    }

    .product-card__img {
        background-color: #f2e7ff;
        border-radius: 8px;
        transition: background-color 0.3s ease-in-out;

        @include bp($bp-desktop-sm) {
            border-radius: 15px;
        }
    }

    .product-card__img-link {
        position: relative;
        display: block;
        padding-bottom: 100%;
        overflow: hidden;

        img {
            @include center-absolute(20px);
            margin: auto;
            max-width: calc(100% - 40px);
            max-height: 100%;
            transform: scale(0.97);
            transition: 0.3s ease-in-out transform, 0.3s ease-in-out filter;
        }
    }

    .product-card__preloader {
        opacity: 1;
    }

    .product-card__img-product {
        z-index: 1;
        display: none;
        animation: fadeIn 0.3s ease-in-out;

        @keyframes fadeIn {
            from {
                opacity: 0;
                transform: scale(0.9);
            }
            to {
                opacity: 1;
                transform: scale(0.97);
            }
        }

        &[lazy="loaded"] {
            display: block;
            & + .product-card__preloader {
                opacity: 0;
            }
        }
    }

    .product-card__colors {
        position: absolute;
        z-index: 5;
        left: 4px;
        bottom: 0;
        max-width: calc(100% - 4px);
        padding: 2px 8px 2px 0;
        background-color: #fff;
        border-top-right-radius: 12px;
        overflow: auto;
        @include bp($bp-desktop-sm) {
            left: 12px;
            padding: 6px 12px 6px 0;
            border-top-right-radius: 20px;
        }
    }

    .product-card__wrap {
        display: flex;
        flex-direction: column;
        flex-grow: 1;
        padding: 8px;
        @include bp($bp-desktop-sm) {
            padding: 12px 20px 8px;
        }
    }

    .product-card__name {
        position: relative;
        z-index: 2;
        display: block;
        margin-bottom: 6px;
        color: var(--primary-text);
        font-size: 12px;
        line-height: 16px;
        letter-spacing: -0.02em;
        @include bp($bp-desktop-sm) {
            margin-bottom: 8px;
            font-size: 14px;
            line-height: 20px;
        }
    }

    .product-card__price {
        display: block;
        margin-top: auto;
    }

    .product-card__controls {
        margin-top: auto;
        padding: 0 8px 8px;

        @include bp($bp-desktop-sm) {
            padding: 0 20px 20px;
        }
    }

    //
    // --mod
    //

    .product-card--sm {
        box-shadow: 0 0 10px #f6ecf6;
        @include bp($bp-desktop-sm) {
            box-shadow: 0 0 15px #f6ecf6;
        }

        .product-card__header {
            padding: 4px 4px 0;
            @include bp($bp-desktop-sm) {
                padding: 8px 8px 0;
            }
        }

        .product-card__wrap {
            padding: 8px;
            @include bp($bp-desktop-sm) {
                padding: 8px 12px;
            }
        }

        .product-card__controls {
            padding: 0 8px 8px;
            @include bp($bp-desktop-sm) {
                padding: 0 12px 12px;
            }
        }

        .product-price__number {
            font-size: 18px;
            line-height: 26px;
        }
    }

    //«My best friend»
    .product-card[data-series="258503"] {
        .product-card__series {
            color: #9d34b1;
        }
        &:hover {
            box-shadow: 0 0 0 1px #e9d8ff;
            .product-card__img {
                background-color: #f9d1ff;
            }
        }
    }

    //«Shimmer Paws»
    .product-card[data-series="258495"] {
        .product-card__series {
            color: #ff479b;
        }
        &:hover {
            box-shadow: 0 0 0 1px #ffdbeb;
            .product-card__img {
                background-color: #ffdbeb;
            }
        }
    }

    //«Yummy»
    .product-card[data-series="258520"] {
        .product-card__series {
            color: #ff9900;
        }
        &:hover {
            box-shadow: 0 0 0 1px #ffeaa1;
            .product-card__img {
                background-color: #fff4ce;
            }
        }
    }

    //«Super Girl»
    .product-card[data-series="258521"] {
        .product-card__series {
            color: #ff3d5e;
        }
        &:hover {
            box-shadow: 0 0 0 1px #ffd6dd;
            .product-card__img {
                background-color: #ffd6dd;
            }
        }
    }

    //«Little Unicorn»
    .product-card[data-series="258522"] {
        .product-card__series {
            color: #00c6e7;
        }
        &:hover {
            box-shadow: 0 0 0 1px #b7f5ff;
            .product-card__img {
                background-color: #cef8ff;
            }
        }
    }

    // Let's be mermaids
    .product-card[data-series="267819"] {
        .product-card__series {
            color: #36a7b2;
        }
        &:hover {
            box-shadow: 0 0 0 1px #b8e3e1;
            .product-card__img {
                background-color: #caeeec;
            }
        }
    }

    // Shimmer wings
    .product-card[data-series="267820"] {
        .product-card__series {
            color: #ff479b;
        }
        &:hover {
            box-shadow: 0 0 0 1px #f5d0f1;
            .product-card__img {
                background-color: #fdd0f8;
            }
        }
    }
</style>
